import classNames from 'classnames'
import { motion, useAnimate, type AnimationSequence } from 'framer-motion'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Marquee from 'react-fast-marquee'
import { Parser, Player } from 'svga'
import { formatDecimalPlaces } from '../../../../helpers/functions/format-decimal-places'
import { useTranslate } from '../../../../hooks/language/use-translate'
import { useGlobalStore } from '../../../../store/global-store'
import type { NotifyGift } from '../../../../types/lives/notifies'
import frame1 from '../assets/1.svga?url'
import frame2 from '../assets/2.svga?url'
import frame3 from '../assets/3.svga?url'
import frame4 from '../assets/4.svga?url'
import base from '../assets/base.svga?url'
import slotEarning from '../assets/slot-earning.svga?url'

interface Props {
  item: NotifyGift
}

function getNumericValue(value: unknown) {
  if (typeof value === 'number' && !Number.isNaN(value)) {
    return value
  }

  if (typeof value === 'string') {
    const raw = value.trim()
    const parsed = Number(raw)

    if (!Number.isNaN(parsed)) {
      return parsed
    }

    const digitsOnly = raw.replace(/[^\d-]/g, '')
    const parsedDigitsOnly = Number(digitsOnly)

    if (digitsOnly && !Number.isNaN(parsedDigitsOnly)) {
      return parsedDigitsOnly
    }
  }

  return 0
}

function getFrameByToastValue(coins: number) {
  if (coins <= 30000) {
    return frame1
  }

  if (coins <= 70000) {
    return frame2
  }

  if (coins <= 100000) {
    return frame3
  }

  return frame4
}

export function Toast({ item }: Props) {
  const toast =
    item?.message?.topGlobal ??
    item?.message?.topSameCountry ??
    item?.message?.topLocal

  const type = toast?.type

  const { liveId, removeNotifyGiftToastSend, removeNotifyGiftToastEarned } =
    useGlobalStore((state) => ({
      liveId: state.liveId,
      removeNotifyGiftToastSend: state.removeNotifyGiftToastSend,
      removeNotifyGiftToastEarned: state.removeNotifyGiftToastEarned,
    }))

  const shouldShowOtherSvga = useMemo(() => {
    if (toast?.type === 'slot') {
      return false
    }

    return (
      (item?.liveId !== undefined && liveId !== item?.liveId) || !toast?.isLuck
    )
  }, [liveId, item?.liveId, toast?.isLuck])

  const toastCoins = useMemo(() => {
    const totalPrice = getNumericValue(toast?.totalPrice)
    if (totalPrice > 0) {
      return totalPrice
    }

    if (toast?.type === 'slot') {
      return getNumericValue(toast?.valueEarning)
    }

    if (toast?.type === 'earned') {
      const returnValues = getNumericValue(toast?.returnValues)
      if (returnValues > 0) {
        return returnValues
      }
    }

    return getNumericValue(toast?.quantity)
  }, [
    toast?.quantity,
    toast?.returnValues,
    toast?.totalPrice,
    toast?.type,
    toast?.valueEarning,
  ])

  const currentSvga = useMemo(() => {
    if (toast?.fileName === 'slot-earning.svga') {
      return slotEarning
    }

    if (toast?.isLuck) {
      return base
    }

    if (toast?.type !== 'slot') {
      return getFrameByToastValue(toastCoins)
    }

    if (toast?.fileName === 'other-live.svga') {
      return getFrameByToastValue(toastCoins)
    }

    if (toast?.fileName === 'base.svga') {
      return getFrameByToastValue(toastCoins)
    }

    return shouldShowOtherSvga
      ? getFrameByToastValue(toastCoins)
      : getFrameByToastValue(toastCoins)
  }, [shouldShowOtherSvga, toast?.fileName, toast?.type, toastCoins])

  const isFrame2 = currentSvga === frame2

  const [scope, animate] = useAnimate()
  const [isAnimating, setIsAnimating] = useState(false)

  const playerRef = useRef<Player>(null)

  const { translate } = useTranslate()

  const handleAllowPlaySound = useCallback(
    (_item: NotifyGift) => {
      if ('ReactNativeWebView' in window && !shouldShowOtherSvga) {
        // @ts-ignore
        window.ReactNativeWebView.postMessage('play-sound')
      }
    },
    [shouldShowOtherSvga],
  )

  useEffect(() => {
    if (isAnimating) {
      const timeout = setTimeout(() => {
        const sequence: AnimationSequence = [
          [scope.current, { x: -window.innerWidth }, { duration: 2 }],
        ]

        animate(sequence).then(() => {
          if (type === 'send' || type === 'slot') {
            removeNotifyGiftToastSend(item)
          }

          if (type === 'earned') {
            removeNotifyGiftToastEarned(item)
          }

          playerRef.current?.destroy()
          setIsAnimating(false)
        })
      }, 3000) // Duração total do toast na tela (5 segundos)

      return () => {
        clearTimeout(timeout)
        handleAllowPlaySound(item) //se
      }
    }
  }, [isAnimating, toast])

  useEffect(() => {
    const canvas = document.getElementById('canvas') as HTMLCanvasElement

    if (canvas) {
      playerRef.current = new Player(canvas)

      const parser = new Parser()

      parser.load(currentSvga).then((svga) => {
        playerRef.current?.mount(svga).then(() => {
          if (playerRef.current) {
            if (toast?.type === 'slot') {
              playerRef.current.config.startFrame = 0
              playerRef.current.config.endFrame = 90
            } else if (shouldShowOtherSvga) {
              playerRef.current.config.startFrame = 30
              playerRef.current.config.endFrame = 80
            } else {
              playerRef.current.config.startFrame = 10
              playerRef.current.config.endFrame = 50
            }
          }

          playerRef.current?.start()
          handleAllowPlaySound(item)

          const sequence: AnimationSequence = [
            [scope.current, { x: 0 }, { duration: 2 }],
            // [
            //   scope.current,
            //   { x: -window.innerWidth },
            //   { delay: 5, duration: 2 },
            // ],
          ]

          if (item) {
            animate(sequence).then(() => {
              setIsAnimating(true)
            })
          }
        })
      })
    }
  }, [])

  const userReceivedName =
    toast?.userReceivedName!.length > 10
      ? `${toast?.userReceivedName?.slice(0, 10)}...`
      : toast?.userReceivedName

  const slotMessage = toast?.text ?? toast?.message

  return (
    <motion.div
      ref={scope}
      className={classNames(
        'h-7 w-full flex flex-row rounded-lg items-center text-white gap-2 relative',
        {
          'mt-24': shouldShowOtherSvga,
          'mt-32': !shouldShowOtherSvga,
        },
      )}
      style={{ x: window.innerWidth }}
    >
      <canvas
        id="canvas"
        className={classNames('absolute w-full -z-10', {
          'h-24': isFrame2,
          'h-36': !isFrame2,
        })}
      ></canvas>

      <div
        className={classNames(
          'w-full flex flex-row justify-start items-center',
          {
            'px-12': shouldShowOtherSvga,
            'px-16': !shouldShowOtherSvga,
          },
        )}
      >
        <img
          src={toast?.invisibleVisitor ? '' : toast?.imgUser}
          className="w-6 h-6 bg-black rounded-full border"
        />

        <div
          className="ml-2 flex justify-start min-w-0"
          style={{ width: '80%' }}
        >
          <Marquee delay={1} speed={80}>
            <span
              className={classNames({
                'mr-2 font-bold': !shouldShowOtherSvga,
                'mr-2 font-bold text-black': shouldShowOtherSvga,
              })}
            >
              {toast?.invisibleVisitor
                ? translate('invisible')
                : toast?.username}
              {type === 'slot' ? (
                <span>&nbsp;{slotMessage}</span>
              ) : (
                <>
                  &nbsp;{translate('toast-earned-global-sent')}&nbsp;
                  <span
                    className={classNames({
                      'text-amber-300': !shouldShowOtherSvga,
                      'text-red-500 font-bold': shouldShowOtherSvga,
                    })}
                  >
                    {toast?.giftName}
                  </span>
                  &nbsp;{translate('toast-earned-global-to')}&nbsp;
                  {userReceivedName}&nbsp;
                  {type === 'earned' && toast?.isLuck && (
                    <span>
                      {translate('toast-earned-global-and')}&nbsp;{' '}
                      {translate('toast-earned-global-earned')}&nbsp;{' '}
                      {formatDecimalPlaces(toast?.returnValues || 0)} coins
                    </span>
                  )}
                </>
              )}
            </span>
          </Marquee>
        </div>
      </div>

      {!shouldShowOtherSvga && (
        <div className="absolute left-1/2" style={{ marginTop: '40%' }}>
          <div
            className="flex flex-row items-center bg-linear-to-r from-amber-400 to-orange-700 justify-center w-30 rounded-xl"
            style={{
              marginLeft: '-50%',
            }}
          >
            <span className="colored-text text-2xl font-bold text-black">
              {type === 'slot'
                ? formatDecimalPlaces(toast?.valueEarning || 0)
                : type === 'send'
                  ? toast?.quantity
                  : formatDecimalPlaces(toast?.returnValues || 0)}
            </span>
          </div>
        </div>
      )}
    </motion.div>
  )
}
